import React, { useState, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { globalState } from '../../../store'

import { Styled } from './styles'
import { GlobalStyled } from '../../component-items/styles'

// Components
import HamburgerMenu from '../../component-items/hamburger-menu'
import { DropDown } from '../../component-items/helpers'

// RBAC Permission Components
import { PermissionNavItem } from '../../rbac/PermissionNavItem'
import { usePermissions } from '../../../hooks/usePermissions'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faList,
  faTachometer,
  faShippingFast,
  faFilePlus,
  faFileCsv,
  faFileChartLine,
  faFileInvoiceDollar,
  faInventory,
  faRampLoading,
  faBoxAlt,
  faExchange,
  faLocation,
  faTasks,
  faLayerGroup,
  faSwatchbook,
  faBuilding,
  faFileCheck,
  faBoxCheck,
  faCubes,
  faBookSpells,
  faMagicWandSparkles,
} from '@fortawesome/pro-duotone-svg-icons'

import { MenuItemProps, MenuSectionProps, MenuSubSectionProps } from './types'

const MenuItem = ({ text, url, icon, children, submenu, open, isOpen, isNew, beta }: MenuItemProps) => {
  const location = useLocation()
  const [active, setActive] = useState(false)
  useEffect(() => {
    var active_temp = !!(url === location.pathname || url === location.pathname + location.search)
    if (url === location.pathname && location.search !== '') {
      active_temp = false
    }
    setActive(active_temp)
  }, [location])

  const body = (
    <>
      {submenu ? (
        <Styled.MenuBullet>
          <span />
        </Styled.MenuBullet>
      ) : (
        <i className="menu__link-icon">
          <FontAwesomeIcon icon={icon} />
        </i>
      )}
      <span className="aside-menu_item">{text}</span>
      {children}
      {isNew ? <div className="aside-menu_new">New</div> : beta ? <div className="aside-menu_beta">Beta</div> : null}
    </>
  )
  return (
    <Styled.MenuItem submenu={submenu} aria-haspopup="true">
      {url ? (
        <Link to={url} onClick={open} className={active ? 'active' : isOpen ? 'open' : ''}>
          {body}
        </Link>
      ) : (
        <a onClick={open} className={active ? 'active' : isOpen ? 'open' : ''}>
          {body}
        </a>
      )}
    </Styled.MenuItem>
  )
}

const MenuSection = ({ text }: MenuSectionProps) => (
  <Styled.MenuSection className="aside-menu_header">
    <h4>{text}</h4>
  </Styled.MenuSection>
)

const MenuSubSection = ({ children, text, icon }: MenuSubSectionProps) => {
  const [isOpen, setOpen] = useState(false)
  return (
    <Styled.MenuItem aria-haspopup="true">
      <MenuItem text={text} icon={icon} open={() => setOpen(!isOpen)} isOpen={isOpen}>
        <GlobalStyled.Arrow className={`aside-menu_item ${isOpen ? 'active' : ''}`} />
      </MenuItem>
      {/* @ts-ignore */}
      <DropDown open={isOpen}>
        <Styled.Submenu>
          <ul>{children}</ul>
        </Styled.Submenu>
      </DropDown>
    </Styled.MenuItem>
  )
}

/**
 * Warehouse Navigation - Permission-gated sidebar for warehouse users
 *
 * Permission mapping from design doc (docs/projects/rbac/03-design-doc.md Section 9):
 * - Dashboard: auth only (no segment requirement)
 * - Automations: settings:viewer
 * - Order List: outbound:viewer
 * - Ship, Create Order, Upload Orders: outbound:operator
 * - Products, Packaging: products:viewer
 * - ASNs: inbound:viewer
 * - Receive: inbound:operator
 * - Returns: inbound:viewer
 * - Inventory List, Locations: inventory:viewer
 * - Move Inventory: inventory:operator
 * - Projects, EDI: vas:viewer
 * - Customers: settings:viewer
 * - Batch List: outbound:viewer
 * - Wave Profiles: outbound:admin
 * - Exit Scan: outbound:operator
 * - Postage Manifests: postage:viewer
 * - Billing: billing:viewer
 * - Reports: reports:viewer
 */
const WarehouseNav = () => {
  const { hasAccess } = usePermissions()

  // Pre-calculate section visibility for cleaner rendering
  const showActionsSection = hasAccess('settings', 'viewer')
  const showOrdersSection = hasAccess('outbound', 'viewer')
  const showInventorySection =
    hasAccess('products', 'viewer') ||
    hasAccess('inbound', 'viewer') ||
    hasAccess('inventory', 'viewer') ||
    hasAccess('vas', 'viewer')
  const showWarehouseSection =
    hasAccess('settings', 'viewer') || hasAccess('outbound', 'viewer') || hasAccess('postage', 'viewer')
  const showDocumentsSection = hasAccess('billing', 'viewer') || hasAccess('reports', 'viewer')

  return (
    <>
      {/* Dashboard - auth only, no segment requirement */}
      <MenuItem text={'Dashboard'} url={'/warehouse/dashboard/'} icon={faTachometer} />

      {/* Actions Section */}
      {showActionsSection && (
        <>
          <MenuSection text={'Actions'} />
          <PermissionNavItem segment="settings" minLevel="viewer">
            <MenuItem text={'Automations'} url={'/action'} icon={faMagicWandSparkles} />
          </PermissionNavItem>
        </>
      )}

      {/* Orders Section */}
      {showOrdersSection && (
        <>
          <MenuSection text={'Orders'} />
          <PermissionNavItem segment="outbound" minLevel="viewer">
            <MenuSubSection text={'Order List'} icon={faList}>
              <MenuItem submenu text={'All'} url={'/order/'} />
              <MenuItem submenu text={'UnFulfilled'} url={'/order/?fulfillment=unfulfilled'} />
              <MenuItem submenu text={'In Fulfillment'} url={'/order/?fulfillment=in_fulfillment'} />
              <MenuItem submenu text={'Fulfilled'} url={'/order/?fulfillment=fulfilled'} />
            </MenuSubSection>
          </PermissionNavItem>
          <PermissionNavItem segment="outbound" minLevel="operator">
            <MenuItem text={'Ship'} url={'/warehouse/start-shipping'} icon={faShippingFast} />
          </PermissionNavItem>
          <PermissionNavItem segment="outbound" minLevel="operator">
            <MenuItem text={'Create Order'} url={'/order/new/'} icon={faFilePlus} />
          </PermissionNavItem>
          <PermissionNavItem segment="outbound" minLevel="operator">
            <MenuItem text={'Upload Orders'} url={'/order/upload-orders/'} icon={faFileCsv} />
          </PermissionNavItem>
        </>
      )}

      {/* Inventory Section */}
      {showInventorySection && (
        <>
          <MenuSection text={'Inventory'} />
          <PermissionNavItem segment="products" minLevel="viewer">
            <MenuItem text={'Products'} url={'/product/'} icon={faCubes} />
          </PermissionNavItem>
          <PermissionNavItem segment="products" minLevel="viewer">
            <MenuItem text={'Packaging'} url={'/package/'} icon={faBoxAlt} />
          </PermissionNavItem>
          <PermissionNavItem segment="inbound" minLevel="viewer">
            <MenuSubSection text={'ASNs'} icon={faRampLoading}>
              <MenuItem submenu text={'ASN List'} url={'/asn/'} />
              {/* Receive requires operator - handled inline */}
              <PermissionNavItem segment="inbound" minLevel="operator">
                <MenuItem submenu text={'Receive'} url={'/warehouse/start-receiving/'} />
              </PermissionNavItem>
            </MenuSubSection>
          </PermissionNavItem>
          <PermissionNavItem segment="inbound" minLevel="viewer">
            <MenuItem text={'Returns'} url={'/return/'} icon={faExchange} />
          </PermissionNavItem>
          <PermissionNavItem segment="inventory" minLevel="viewer">
            <MenuSubSection text={'Inventory'} icon={faRampLoading}>
              <MenuItem submenu text={'Inventory List'} url={'/inventory/'} />
              <MenuItem submenu text={'Transaction List'} url={'/inventory/transactions/'} />
              {/* Move Inventory requires operator - handled inline */}
              <MenuItem submenu text={'Containers'} url={'/inventory/container/'} />
              <PermissionNavItem segment="inventory" minLevel="operator">
                <MenuItem submenu text={'Move Inventory'} url={'/inventory/move/'} />
                <MenuItem submenu text={'Container Move'} url={'/inventory/container/move'} />
              </PermissionNavItem>
            </MenuSubSection>
          </PermissionNavItem>
          <PermissionNavItem segment="inventory" minLevel="viewer">
            <MenuItem text={'Locations'} url={'/location/'} icon={faLocation} />
          </PermissionNavItem>
          <PermissionNavItem segment="vas" minLevel="viewer">
            <MenuItem text={'Projects'} url={'/project/?filter_status=140'} icon={faTasks} />
          </PermissionNavItem>
          <PermissionNavItem segment="vas" minLevel="viewer">
            <MenuItem text={'EDI'} url={'/edi/'} icon={faBookSpells} />
          </PermissionNavItem>
        </>
      )}

      {/* Warehouse Section */}
      {showWarehouseSection && (
        <>
          <MenuSection text={'Warehouse'} />
          <PermissionNavItem segment="settings" minLevel="viewer">
            <MenuItem text={'Customers'} url={'/warehouse/companies'} icon={faBuilding} />
          </PermissionNavItem>
          <PermissionNavItem segment="outbound" minLevel="viewer">
            <MenuItem text={'Batch List'} url={'/batch/'} icon={faLayerGroup} />
          </PermissionNavItem>
          <PermissionNavItem segment="outbound" minLevel="admin">
            <MenuItem text={'Wave Profiles'} url={'/batch/profile/'} icon={faSwatchbook} />
          </PermissionNavItem>
          <PermissionNavItem segment="outbound" minLevel="operator">
            <MenuItem text={'Exit Scan'} url={'/warehouse/exit-scan/'} icon={faBoxCheck} />
          </PermissionNavItem>
          <PermissionNavItem segment="postage" minLevel="viewer">
            <MenuItem text={'Postage Manifests'} url={'/postage/manifests/'} icon={faFileCheck} />
          </PermissionNavItem>
        </>
      )}

      {/* Documents Section */}
      {showDocumentsSection && (
        <>
          <MenuSection text={'Documents'} />
          <PermissionNavItem segment="billing" minLevel="viewer">
            <MenuItem text={'Billing'} url={'/billing/company/'} icon={faFileInvoiceDollar} />
          </PermissionNavItem>
          <PermissionNavItem segment="reports" minLevel="viewer">
            <MenuItem text={'Reports'} url={'/company/reports/'} icon={faFileChartLine} />
          </PermissionNavItem>
        </>
      )}
    </>
  )
}

/**
 * Merchant Navigation - Permission-gated sidebar for merchant users
 *
 * Permission mapping from design doc (docs/projects/rbac/03-design-doc.md Section 9):
 * - Dashboard: auth only (no segment requirement)
 * - Automations: settings:viewer
 * - Order List: outbound:viewer
 * - Create Order, Upload Orders: outbound:operator
 * - Products, Packaging: products:viewer
 * - ASNs: inbound:viewer
 * - Returns: inbound:viewer
 * - Inventory Transaction List: inventory:viewer
 * - Projects, EDI: vas:viewer
 * - Billing: billing:viewer
 * - Reports: reports:viewer
 */
const MerchantNav = () => {
  const { hasAccess } = usePermissions()

  // Pre-calculate section visibility for cleaner rendering
  const showActionsSection = hasAccess('settings', 'viewer')
  const showOrdersSection = hasAccess('outbound', 'viewer')
  const showInventorySection =
    hasAccess('products', 'viewer') ||
    hasAccess('inbound', 'viewer') ||
    hasAccess('inventory', 'viewer') ||
    hasAccess('vas', 'viewer')
  const showDocumentsSection = hasAccess('billing', 'viewer') || hasAccess('reports', 'viewer')

  return (
    <>
      {/* Dashboard - auth only, no segment requirement */}
      <MenuItem text={'Dashboard'} url={'/company/dashboard/'} icon={faTachometer} />

      {/* Actions Section */}
      {showActionsSection && (
        <>
          <MenuSection text={'Actions'} />
          <PermissionNavItem segment="settings" minLevel="viewer">
            <MenuItem text={'Automations'} url={'/action'} icon={faMagicWandSparkles} />
          </PermissionNavItem>
        </>
      )}

      {/* Orders Section */}
      {showOrdersSection && (
        <>
          <MenuSection text={'Orders'} />
          <PermissionNavItem segment="outbound" minLevel="viewer">
            <MenuSubSection text={'Order List'} icon={faList}>
              <MenuItem submenu text={'All'} url={'/order/'} />
              <MenuItem submenu text={'UnFulfilled'} url={'/order/?fulfillment=unfulfilled'} />
              <MenuItem submenu text={'In Fulfillment'} url={'/order/?fulfillment=in_fulfillment'} />
              <MenuItem submenu text={'Fulfilled'} url={'/order/?fulfillment=fulfilled'} />
            </MenuSubSection>
          </PermissionNavItem>
          <PermissionNavItem segment="outbound" minLevel="operator">
            <MenuItem text={'Create Order'} url={'/order/new/'} icon={faFilePlus} />
          </PermissionNavItem>
          <PermissionNavItem segment="outbound" minLevel="operator">
            <MenuItem text={'Upload Orders'} url={'/order/upload-orders/'} icon={faFileCsv} />
          </PermissionNavItem>
        </>
      )}

      {/* Inventory Section */}
      {showInventorySection && (
        <>
          <MenuSection text={'Inventory'} />
          <PermissionNavItem segment="products" minLevel="viewer">
            <MenuItem text={'Products'} url={'/product/'} icon={faInventory} />
          </PermissionNavItem>
          <PermissionNavItem segment="products" minLevel="viewer">
            <MenuItem text={'Packaging'} url={'/package/'} icon={faBoxAlt} />
          </PermissionNavItem>
          <PermissionNavItem segment="inbound" minLevel="viewer">
            <MenuItem text={'ASNs'} url={'/asn/'} icon={faRampLoading} />
          </PermissionNavItem>
          <PermissionNavItem segment="inbound" minLevel="viewer">
            <MenuItem text={'Returns'} url={'/return/'} icon={faExchange} />
          </PermissionNavItem>
          <PermissionNavItem segment="inventory" minLevel="viewer">
            <MenuSubSection text={'Inventory'} icon={faRampLoading}>
              <MenuItem submenu text={'Inventory Transaction List'} url={'/inventory/transactions/'} />
            </MenuSubSection>
          </PermissionNavItem>
          <PermissionNavItem segment="vas" minLevel="viewer">
            <MenuItem text={'Projects'} url={'/project/?filter_status=140'} icon={faTasks} />
          </PermissionNavItem>
          <PermissionNavItem segment="vas" minLevel="viewer">
            <MenuItem text={'EDI'} url={'/edi/'} icon={faBookSpells} />
          </PermissionNavItem>
        </>
      )}

      {/* Documents Section */}
      {showDocumentsSection && (
        <>
          <MenuSection text={'Documents'} />
          <PermissionNavItem segment="billing" minLevel="viewer">
            <MenuItem text={'Billing'} url={'/billing/company/'} icon={faFileInvoiceDollar} />
          </PermissionNavItem>
          <PermissionNavItem segment="reports" minLevel="viewer">
            <MenuItem text={'Reports'} url={'/company/reports/'} icon={faFileChartLine} />
          </PermissionNavItem>
        </>
      )}
    </>
  )
}

export const NavigationAside = () => {
  const {
    state: { userIsWarehouse, navOpen, navStayOpen, isImpersonating },
    dispatch,
  } = globalState()

  return (
    <Styled.Aside
      onMouseEnter={() => (!navOpen ? dispatch({ type: 'navOpen' }) : null)}
      onMouseLeave={() => (!navStayOpen && navOpen ? dispatch({ type: 'navClose' }) : null)}
      {...{ navOpen, isImpersonating }}
    >
      <Styled.AsideBrand {...{ navOpen }}>
        <Styled.BrandLogo {...{ navOpen }}>
          <a href="/">
            <img alt="Logo" width="100px" src={'/api/static/assets/media/logos/kase-logo.png'} />
          </a>
        </Styled.BrandLogo>
        <Styled.AsideHamburgerMenu onClick={() => dispatch({ type: navStayOpen ? 'navCloseSave' : 'navOpenSave' })}>
          <HamburgerMenu active={navStayOpen} />
        </Styled.AsideHamburgerMenu>
      </Styled.AsideBrand>
      <Styled.AsideMenu>
        <ul>{userIsWarehouse ? <WarehouseNav /> : <MerchantNav />}</ul>
      </Styled.AsideMenu>
      {/*
            <Styled.AsideFooter>
                <AsideItem url={"#"} icon={faCog} />
                <AsideItem url={"http://help.shippingtree.co/"} icon={faWrench} />
                <AsideItem url={"#"} icon={faBell} />
                <div className={"aside__footer-item " + (isOpen ? "active" : "")}>
                    <button onClick={() => setOpen(!isOpen)} type="button" aria-haspopup="true" aria-expanded="false">
                        <i><FontAwesomeIcon icon={faPlusCircle} /></i>
                    </button>
                    {isOpen ? <ExportTools title={"Export Tools"} print={"#"} copy={"#"} excel={"#"} csv={"#"} pdf={"#"} /> : null}
                </div>
                <AsideItem url={"#"} icon={faCalendarDay} />
            </Styled.AsideFooter>
            */}
    </Styled.Aside>
  )
}
